// Editor dialog: one admin edits one template.
//
// * Lists (opening_rules, restricted_claim_rules, …) are edited as one rule
//   per line and sent back to the server as string arrays.
// * The template_type is locked after creation — changing it would mean a
//   whole different archetype.
// * Publish bumps the version and stamps published_at (server-side).

import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { templatesApi } from '../../../api';
import { SECTION_FIELDS } from './constants';

const EMPTY_CONTENT = SECTION_FIELDS.reduce((acc, f) => {
  acc[f.key] = f.type === 'list' ? [] : '';
  return acc;
}, {});

function listToText(list) {
  return Array.isArray(list) ? list.join('\n') : '';
}
function textToList(text) {
  return (text || '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
}

export default function TemplateEditorDialog({ template, open, onOpenChange, onChanged }) {
  const [form, setForm] = useState({ name: '', description: '', content: EMPTY_CONTENT });
  const [saving, setSaving] = useState(false);
  const [acting, setActing] = useState(false);

  useEffect(() => {
    if (!open || !template) return;
    setForm({
      name: template.name || '',
      description: template.description || '',
      content: { ...EMPTY_CONTENT, ...(template.content || {}) },
    });
  }, [open, template]);

  if (!template) return null;

  const updateSection = (key, value) => {
    setForm((prev) => ({ ...prev, content: { ...prev.content, [key]: value } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await templatesApi.update(template.id, {
        name: form.name,
        description: form.description,
        content: form.content,
      });
      toast.success('Template saved');
      onChanged?.();
    } catch (err) {
      toast.error(err.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    setActing(true);
    try {
      await templatesApi.publish(template.id);
      toast.success('Template published (version bumped)');
      onChanged?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || 'Failed to publish');
    } finally {
      setActing(false);
    }
  };

  const archive = async () => {
    if (!confirm('Archive this template? It will no longer be available to new compiles.')) return;
    setActing(true);
    try {
      await templatesApi.archive(template.id);
      toast.success('Template archived');
      onChanged?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || 'Failed to archive');
    } finally {
      setActing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {template.name} — v{template.version} ({template.status})
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            />
          </div>
          <div>
            <Label>Description (shown to dealers on their agent card)</Label>
            <Textarea
              value={form.description}
              rows={2}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            />
          </div>

          {SECTION_FIELDS.map((f) => (
            <div key={f.key}>
              <Label>{f.label}{f.type === 'list' && ' (one per line)'}</Label>
              <Textarea
                rows={f.type === 'list' ? 5 : 3}
                value={f.type === 'list'
                  ? listToText(form.content[f.key])
                  : form.content[f.key] || ''}
                onChange={(e) => {
                  const v = f.type === 'list' ? textToList(e.target.value) : e.target.value;
                  updateSection(f.key, v);
                }}
              />
            </div>
          ))}
        </div>

        <DialogFooter className="flex flex-wrap gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button variant="outline" disabled={acting || template.status === 'archived'} onClick={archive}>
            Archive
          </Button>
          <Button disabled={saving} onClick={save}>Save draft</Button>
          <Button variant="default" disabled={acting} onClick={publish}>
            Publish (v{template.version + 1})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
